import { Injectable, inject } from '@angular/core';
import {
  CanActivate,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { AuthService } from '../services/auth.service';
import { UnauthorizedAccessService } from '../services/unauthorized-access.service';
import { AppRole } from '../models/permissions.model';
import { environment } from '../../../environments/environment';

/**
 * Guard for review-professional route
 * 
 * Validates:
 * 1. User has SUPER_ADMIN, BACKOFFICE_ADMIN, or BACKOFFICE_PROFESSIONAL role
 * 
 * Note: Mode (review vs detail) is now determined automatically by the component
 * based on the current registration step, not by queryParams
 */
@Injectable({
  providedIn: 'root',
})
export class ReviewAccessGuard implements CanActivate {
  private router = inject(Router);
  private authService = inject(AuthService);
  private unauthorizedService = inject(UnauthorizedAccessService);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean | UrlTree> {
    // Check if guards are disabled in environment (ONLY in development builds)
    const disableGuards = (environment as any).disableAuthGuards;
    if (disableGuards && !environment.production) {
      console.log('🔓 ReviewAccessGuard bypassed (development only)');
      return Promise.resolve(true);
    }

    // Production safety: ensure bypass is impossible in production
    if (disableGuards && environment.production) {
      console.error('⚠️ Security: disableAuthGuards ignored in production build');
    }

    // Wait for startup silent-refresh before checking auth state.
    // Without this, F5 always redirects to /login because _accessToken is null
    // until the BFF silent-refresh responds.
    return this.authService.initialized.then(() => {
    // 1. Check if user is authenticated
    const token = this.authService.getAccessToken();
    if (!token) {
      console.log('❌ ReviewAccessGuard: User not authenticated, redirecting to /login');
      return this.router.createUrlTree(['/login']);
    }

    // 2. Check if user has required role
    const user = this.authService.getCurrentUser();
    const allowedRoles = [AppRole.SUPER_ADMIN, AppRole.BACKOFFICE_ADMIN, AppRole.BACKOFFICE_PROFESSIONAL];
    const hasRole = user?.roles?.some((role: string) => allowedRoles.includes(role as AppRole));

    if (!hasRole) {
      console.warn('⚠️ [REVIEW-ACCESS-GUARD→/unauthorized] User lacks required roles');
      console.warn('  ➡ Route:', state.url);
      console.warn('  ➡ Required roles:', allowedRoles);
      console.warn('  ➡ User roles:', user?.roles);
      this.unauthorizedService.setFromGuard(true);
      return this.router.createUrlTree(['/unauthorized']);
    }

    // 3. Get professionalId from route params
    const professionalId = route.paramMap.get('id');
    if (!professionalId) {
      console.error('❌ ReviewAccessGuard: Missing professionalId in route');
      return this.router.createUrlTree(['/axa/dashboard']);
    }

    // Access granted - component will determine mode based on current step
    console.log('✅ ReviewAccessGuard: Access granted for professional', professionalId);
    return true;
    }); // initialized.then()
  }
}
